import React, { useState } from "react";
import { format } from "date-fns";
import { Building, FileText, Plus, Wallet } from "lucide-react";
import MobileDetailLayout from "../../components/mobile/mobile-detail-layout";
import MobileInfoCard from "../../components/mobile/mobile-info-card";
import type { StatItem } from "../../components/mobile/types";
import DataTable, {
  DataTableAction,
  TableRowData,
} from "../../components/data-table";
import SelectInput from "../../components/select-input";
import TabBar from "../../components/tab-bar";
import TabButton from "../../components/tab-button";
import { Badge } from "../../components/ui/badge";
import type { Customer } from "../../lib/api";
import {
  activitiesTableColumns,
  invoicesTableColumns,
  quotesTableColumns,
  sitesTableColumns,
} from "../../lib/table-columns";
import {
  MOVEMENT_STATUS_OPTIONS,
  MOVEMENT_SUB_TYPE_OPTIONS,
} from "../site-details/movement-filter-options";

type Pagination = NonNullable<
  React.ComponentProps<typeof DataTable>["pagination"]
>;

interface RowsSection {
  rows: TableRowData[];
  actions: DataTableAction[];
}

export interface CustomerDetailsMobileProps {
  customer: Customer;
  onBack: () => void;
  onRefresh: () => Promise<unknown>;
  onAddSite: () => void;
  canAddSite: boolean;
  insights: React.ReactNode;
  sites: RowsSection;
  movements: RowsSection & {
    totalItems: number;
    isFetching: boolean;
    pagination: Pagination;
    subTypeFilter: string;
    onSubTypeFilterChange: (value: string) => void;
    statusFilter: string;
    onStatusFilterChange: (value: string) => void;
  };
  quotes: RowsSection;
  invoices: RowsSection;
}

type CustomerTab = "sites" | "movements" | "quotes" | "invoices";

const TYPE_LABELS: Record<Customer["type"], string> = {
  ORGANIZATION: "Organization",
  INDIVIDUAL: "Individual",
};

const CustomerDetailsMobile: React.FC<CustomerDetailsMobileProps> = ({
  customer,
  onBack,
  onRefresh,
  onAddSite,
  canAddSite,
  insights,
  sites,
  movements,
  quotes,
  invoices,
}) => {
  const [tab, setTab] = useState<CustomerTab>("sites");

  const stats: StatItem[] = [
    {
      label: "Total Sites",
      value: customer.sites?.length || 0,
      icon: <Building className="w-5 h-5 text-primary-600" />,
    },
    {
      label: "Total Quotes",
      value: customer.quotations?.length || 0,
      icon: <FileText className="w-5 h-5 text-blue-600" />,
    },
    {
      label: "Total Invoices",
      value: customer.invoices?.length || 0,
      icon: <Wallet className="w-5 h-5 text-purple-600" />,
    },
  ];

  const tabs: { key: CustomerTab; label: string; count: number }[] = [
    { key: "sites", label: "Sites", count: sites.rows.length },
    { key: "movements", label: "Movements", count: movements.totalItems },
    { key: "quotes", label: "Quotations", count: quotes.rows.length },
    { key: "invoices", label: "Invoices", count: invoices.rows.length },
  ];

  const location = [
    customer.street,
    customer.ward,
    customer.district,
    customer.region,
  ]
    .filter(Boolean)
    .join(", ");
  const typeLabel = TYPE_LABELS[customer.type] ?? customer.type;

  const renderTab = () => {
    switch (tab) {
      case "sites":
        return (
          <DataTable
            columns={sitesTableColumns}
            data={sites.rows}
            actions={sites.actions}
          />
        );
      case "movements":
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <SelectInput
                value={movements.subTypeFilter}
                options={MOVEMENT_SUB_TYPE_OPTIONS}
                placeholder="Subtype"
                onChange={movements.onSubTypeFilterChange}
              />
              <SelectInput
                value={movements.statusFilter}
                options={MOVEMENT_STATUS_OPTIONS}
                placeholder="Status"
                onChange={movements.onStatusFilterChange}
              />
            </div>
            <DataTable
              columns={activitiesTableColumns}
              data={movements.rows}
              actions={movements.actions}
              loading={movements.isFetching}
              pagination={movements.pagination}
            />
          </div>
        );
      case "quotes":
        return (
          <DataTable
            columns={quotesTableColumns}
            data={quotes.rows}
            actions={quotes.actions}
          />
        );
      case "invoices":
        return (
          <DataTable
            columns={invoicesTableColumns}
            data={invoices.rows}
            actions={invoices.actions}
          />
        );
    }
  };

  return (
    <MobileDetailLayout
      eyebrow="Customer"
      title={customer.fullName}
      subtitle={
        [customer.region, customer.country].filter(Boolean).join(", ") ||
        undefined
      }
      badge={<Badge variant="outline">{typeLabel}</Badge>}
      onBack={onBack}
      onRefresh={onRefresh}
      primaryAction={{
        label: "Add Site",
        icon: Plus,
        disabled: !canAddSite,
        onClick: () => {
          setTab("sites");
          onAddSite();
        },
      }}
      stats={stats}
      insights={insights}
    >
      <MobileInfoCard
        title="Details"
        rows={[
          customer.phoneNumber && {
            label: "Phone",
            value: customer.phoneNumber,
            href: `tel:${customer.phoneNumber}`,
          },
          { label: "Type", value: typeLabel },
          customer.pointOfContactName && {
            label: "Point of contact",
            value: customer.pointOfContactName,
          },
          customer.pointOfContactPhone && {
            label: "Contact phone",
            value: customer.pointOfContactPhone,
            href: `tel:${customer.pointOfContactPhone}`,
          },
          !!location && { label: "Location", value: location },
          customer.country && { label: "Country", value: customer.country },
          customer.tin && { label: "TIN", value: customer.tin },
          customer.vrn && { label: "VRN", value: customer.vrn },
          customer.createdAt && {
            label: "Customer since",
            value: format(new Date(customer.createdAt), "MMM dd, yyyy"),
          },
        ]}
      />

      <TabBar>
        {tabs.map((item) => (
          <TabButton
            key={item.key}
            label={item.label}
            count={item.count}
            active={tab === item.key}
            onClick={() => setTab(item.key)}
          />
        ))}
      </TabBar>

      {renderTab()}
    </MobileDetailLayout>
  );
};

export const CustomerNotFoundMobile: React.FC<{ onBack: () => void }> = ({
  onBack,
}) => (
  <MobileDetailLayout title="Customer not found" onBack={onBack}>
    <p className="text-sm text-gray-500">
      This customer could not be loaded. Go back to the customers list and try
      again.
    </p>
  </MobileDetailLayout>
);

export default CustomerDetailsMobile;
